import { DotsThreeVerticalIcon } from "@phosphor-icons/react";
import { AnchoredPanel, cn } from "@bafe/ui";
import {
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
	type KeyboardEvent as ReactKeyboardEvent,
} from "react";

export interface RowMenuItem {
	id: string;
	label: string;
	onSelect: () => void;
	disabled?: boolean;
	danger?: boolean;
}

export function RowMenu({ label, items }: { label: string; items: RowMenuItem[] }) {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const menuId = useId();

	const close = useCallback((restoreFocus = true) => {
		setOpen(false);
		if (restoreFocus) triggerRef.current?.focus();
	}, []);

	useEffect(() => {
		if (!open) return;
		const first = panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])');
		first?.focus();
	}, [open]);

	const moveFocus = (direction: 1 | -1 | "first" | "last") => {
		const itemsInMenu = Array.from(
			panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? [],
		);
		if (itemsInMenu.length === 0) return;
		const active = document.activeElement as HTMLElement | null;
		const index = active ? itemsInMenu.indexOf(active) : -1;
		let next = 0;
		if (direction === "first") next = 0;
		else if (direction === "last") next = itemsInMenu.length - 1;
		else next = (index + direction + itemsInMenu.length) % itemsInMenu.length;
		itemsInMenu[next]?.focus();
	};

	const onTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
		if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			setOpen(true);
		}
		if (event.key === "Escape" && open) {
			event.preventDefault();
			close();
		}
	};

	return (
		<div className="flex justify-end">
			<button
				ref={triggerRef}
				type="button"
				aria-label={label}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-controls={menuId}
				onClick={(event) => {
					event.stopPropagation();
					setOpen((current) => !current);
				}}
				onKeyDown={onTriggerKeyDown}
				className={cn(
					"inline-flex size-9 shrink-0 items-center justify-center rounded-full border-0 bg-transparent",
					"text-(--text-muted) transition-colors duration-150",
					"hover:bg-(--surface-sunken) hover:text-(--text-primary)",
					"focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-brand-500)",
					open && "bg-(--surface-sunken) text-(--text-primary)",
				)}
			>
				<DotsThreeVerticalIcon className="size-5" aria-hidden="true" />
			</button>
			<AnchoredPanel
				ref={panelRef}
				open={open}
				anchorRef={triggerRef}
				align="end"
				onDismiss={() => setOpen(false)}
				id={menuId}
				role="menu"
				aria-label={label}
				tabIndex={-1}
				onKeyDown={(event) => {
					switch (event.key) {
						case "Escape":
							event.preventDefault();
							close();
							break;
						case "ArrowDown":
							event.preventDefault();
							moveFocus(1);
							break;
						case "ArrowUp":
							event.preventDefault();
							moveFocus(-1);
							break;
						case "Home":
							event.preventDefault();
							moveFocus("first");
							break;
						case "End":
							event.preventDefault();
							moveFocus("last");
							break;
						case "Tab":
							close(false);
							break;
						default:
							break;
					}
				}}
				className="min-w-40 py-1"
			>
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						role="menuitem"
						tabIndex={-1}
						disabled={item.disabled}
						aria-disabled={item.disabled || undefined}
						onClick={() => {
							if (item.disabled) return;
							close(false);
							item.onSelect();
						}}
						className={cn(
							"flex w-full rounded-(--radius-sm) px-2.5 py-2 text-left text-sm outline-none",
							"transition-colors duration-150",
							item.danger ? "text-(--color-critical-700)" : "text-(--text-primary)",
							"hover:bg-(--surface-sunken) focus-visible:bg-(--surface-sunken)",
							"disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent",
						)}
					>
						{item.label}
					</button>
				))}
			</AnchoredPanel>
		</div>
	);
}
